// /admin/people: People & access for account administrators (0022).
//
// Lists the people of the administrator's sites with their account, access and invitation.
// An administrator invites a person (an existing person record) by email with site access,
// resends or expires an invitation, suspends, reactivates or disables an account, and gives
// or removes access per site. Every change is a command the server checks and records with
// who, when and why. Access is software access only: signing authority is a licence (the
// licence register), and an administrator never changes their own account or access.
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useId, useState } from 'react'
import { ErrorBanner } from '@/components/evidence'
import { Badge, Banner, Button, Card, Checkbox, EmptyState, Field, PageHeader, Select, Sheet, Spinner, TextArea, TextInput } from '@/components/ui'
import { type AccountAdminView, type AdminPerson, CAPABILITY_LABELS, GLOBAL_CAPABILITIES, SITE_CAPABILITIES } from '@/domain/identity'
import { CopyButton } from '@/features/demo/parts'
import { runCommand, newIdempotencyKey } from '@/lib/commands'
import { fmtDate, fmtDateTime } from '@/lib/format'
import { emailProblem, normaliseEmail } from '@/lib/identity'
import { useAccountAdminView, useCommand, useMe } from '@/lib/queries'
import { emailInvitation, type InviteEmailResult } from './inviteEmail'
import {
  accessChanges,
  accessKey,
  type AccessSet,
  accessSummary,
  accessToGrants,
  currentAccess,
  DEFAULT_PEOPLE_SORT,
  EMPTY_PEOPLE_FILTER,
  filterPeople,
  INVITATION_DAYS,
  invitationMessage,
  nextSort,
  openInvitation,
  type PeopleSort,
  type PeopleSortKey,
  personEmail,
  type PeopleFilter,
  personActions,
  personState,
  sortPeople,
  STATE_LABEL,
  STATE_TONE,
  toggleAccess,
} from './model'

type Sites = AccountAdminView['sites']
type Lifecycle = 'suspend' | 'reactivate' | 'disable'

export function PeoplePage() {
  const me = useMe()
  const admin = Boolean(me.data?.global_capabilities?.includes('administer_accounts'))
  const q = useAccountAdminView(admin)
  const [filter, setFilter] = useState<PeopleFilter>(EMPTY_PEOPLE_FILTER)
  const [inviting, setInviting] = useState<AdminPerson | null>(null)
  const [resending, setResending] = useState<AdminPerson | null>(null)
  const [expiring, setExpiring] = useState<AdminPerson | null>(null)
  const [lifecycle, setLifecycle] = useState<{ person: AdminPerson; action: Lifecycle } | null>(null)
  const [access, setAccess] = useState<AdminPerson | null>(null)
  const [adding, setAdding] = useState(false)
  const [managing, setManaging] = useState<AdminPerson | null>(null)
  const [sort, setSort] = useState<PeopleSort>(DEFAULT_PEOPLE_SORT)
  const ids = { text: useId(), state: useId() }

  if (me.isPending || (admin && q.isPending)) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }
  if (!admin) {
    return (
      <div className="max-w-3xl">
        <PageHeader title="People & access" />
        <Banner tone="info" title="Only an account administrator can see this page">
          Ask an account administrator if you need access to another site.
        </Banner>
      </div>
    )
  }
  if (!q.data) return <ErrorBanner error={q.error ?? new Error('People & access is not available.')} title="Could not load people and access" />

  const data = q.data
  const rows = sortPeople(filterPeople(data.people, filter), sort, data.sites)
  // An action from the Manage sheet closes it and opens the action's own sheet.
  const then = (open: () => void) => {
    setManaging(null)
    open()
  }
  return (
    <div className="space-y-4" data-testid="people-page">
      <PageHeader
        title="People & access"
        subtitle={`${data.people.length} ${data.people.length === 1 ? 'person' : 'people'} on ${data.sites.map((s) => s.name).join(', ') || 'your sites'}.`}
      />
      <Banner tone="info" title="Access is not signing authority">
        Access lets a person see a site, apply for permits there or add evidence. Who may sign in a PTW role is decided by their
        licence in the licence register. Each change is kept with your name, the time and your reason; you cannot change your
        own account or access.
      </Banner>
      {!data.invitations_enabled ? (
        <Banner tone="synthetic" title="Synthetic demonstration">
          The demo uses personas, so nobody is invited by email here. Accounts can still be suspended and access changed.
        </Banner>
      ) : null}
      {data.invitations_enabled ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => setAdding(true)} disabled={(data.organisations ?? []).length === 0} data-testid="open-add-person">
            Add and invite a person
          </Button>
          <p className="text-sm text-ink-soft">
            {(data.organisations ?? []).length === 0
              ? 'No company is recorded for your account yet, so nobody can be added. Provisioning records it once per environment.'
              : null}
          </p>
        </div>
      ) : null}

      <Card>
        <div className="grid gap-x-4 sm:grid-cols-2" role="group" aria-label="Filter people">
          <Field label="Search" htmlFor={ids.text}>
            <TextInput id={ids.text} value={filter.text} onChange={(e) => setFilter({ ...filter, text: e.target.value })} placeholder="Name, organisation or email" />
          </Field>
          <Field label="Account" htmlFor={ids.state}>
            <Select
              id={ids.state}
              value={filter.state}
              onChange={(e) => setFilter({ ...filter, state: e.target.value as PeopleFilter['state'] })}
              placeholder="Everyone"
              options={(Object.keys(STATE_LABEL) as (keyof typeof STATE_LABEL)[]).map((s) => ({ value: s, label: STATE_LABEL[s] }))}
            />
          </Field>
        </div>
        <p className="text-sm text-ink-soft" aria-live="polite">
          Showing {rows.length} of {data.people.length}.
        </p>
      </Card>

      {rows.length === 0 ? (
        <EmptyState title="Nobody matches">Add and invite a person to start.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-white">
          <table className="w-full min-w-[720px] border-collapse text-sm" aria-label="People" data-testid="people-list">
            <thead className="bg-paper text-left">
              <tr>
                <SortHeader label="Name" k="name" sort={sort} onSort={setSort} />
                <SortHeader label="Email" k="email" sort={sort} onSort={setSort} />
                <SortHeader label="Employer" k="organisation" sort={sort} onSort={setSort} />
                <SortHeader label="Status" k="state" sort={sort} onSort={setSort} />
                <SortHeader label="Access" k="access" sort={sort} onSort={setSort} />
                <th className="px-3 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const state = personState(p)
                const summary = accessSummary(p, data.sites)
                return (
                  <tr key={p.person_id} className="border-t border-line align-top" data-testid="person-row" data-person={p.display_name} data-state={state}>
                    <td className="px-3 py-2 font-semibold">
                      {p.display_name}
                      {p.staff_reference ? <span className="block text-[13px] font-normal text-ink-soft">{p.staff_reference}</span> : null}
                    </td>
                    <td className="px-3 py-2 break-all">{personEmail(p) ?? <span className="text-ink-soft">-</span>}</td>
                    <td className="px-3 py-2">{p.organisation ?? <span className="text-ink-soft">-</span>}</td>
                    <td className="px-3 py-2"><Badge tone={STATE_TONE[state]}>{STATE_LABEL[state]}</Badge></td>
                    <td className="px-3 py-2" data-testid="person-access">{summary.length ? summary.join('; ') : <span className="text-ink-soft">None</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="secondary" onClick={() => setManaging(p)} data-testid="open-person">Manage</Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {managing ? (
        <Sheet open title={managing.display_name} onClose={() => setManaging(null)}>
          <PersonCard
            person={managing}
            sites={data.sites}
            invitationsEnabled={data.invitations_enabled}
            onInvite={() => then(() => setInviting(managing))}
            onResend={() => then(() => setResending(managing))}
            onExpire={() => then(() => setExpiring(managing))}
            onLifecycle={(action) => then(() => setLifecycle({ person: managing, action }))}
            onAccess={() => then(() => setAccess(managing))}
          />
        </Sheet>
      ) : null}

      {adding ? (
        <AddPersonSheet organisations={data.organisations ?? []} sites={data.sites} onClose={() => setAdding(false)} />
      ) : null}
      {inviting ? <InviteSheet person={inviting} sites={data.sites} onClose={() => setInviting(null)} /> : null}
      {resending ? <ResendSheet person={resending} onClose={() => setResending(null)} /> : null}
      {expiring ? <ExpireSheet person={expiring} onClose={() => setExpiring(null)} /> : null}
      {lifecycle ? <LifecycleSheet {...lifecycle} onClose={() => setLifecycle(null)} /> : null}
      {access ? <AccessSheet person={access} sites={data.sites} onClose={() => setAccess(null)} /> : null}
    </div>
  )
}

function SortHeader({ label, k, sort, onSort }: { label: string; k: PeopleSortKey; sort: PeopleSort; onSort: (s: PeopleSort) => void }) {
  const active = sort.key === k
  return (
    <th className="px-3 py-2 font-semibold" aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="tap inline-flex items-center gap-1 hover:underline" onClick={() => onSort(nextSort(sort, k))}>
        {label}
        <span aria-hidden className={active ? '' : 'text-ink-soft/50'}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
      </button>
    </th>
  )
}

function PersonCard(props: {
  person: AdminPerson
  sites: Sites
  invitationsEnabled: boolean
  onInvite: () => void
  onResend: () => void
  onExpire: () => void
  onLifecycle: (a: Lifecycle) => void
  onAccess: () => void
}) {
  const p = props.person
  const state = personState(p)
  const inv = openInvitation(p)
  const actions = personActions(p, props.invitationsEnabled)
  const summary = accessSummary(p, props.sites)
  return (
    <Card className="h-full">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold break-words">{p.display_name}</p>
          <p className="text-sm text-ink-soft">{[p.organisation, p.staff_reference].filter(Boolean).join(' - ') || 'No organisation recorded'}</p>
        </div>
        <Badge tone={STATE_TONE[state]}>{STATE_LABEL[state]}</Badge>
      </div>
      <dl className="mt-3 grid gap-y-2 text-sm">
        {p.account?.email ? (
          <div>
            <dt className="text-ink-soft">Signs in as</dt>
            <dd className="break-words">{p.account.email}</dd>
          </div>
        ) : null}
        {inv ? (
          <div>
            <dt className="text-ink-soft">Invitation</dt>
            <dd className="break-words">
              {inv.email}: {inv.status === 'pending' ? `expires ${fmtDateTime(inv.expires_at)}` : `expired ${fmtDateTime(inv.closed_at ?? inv.expires_at)}`}
              {inv.closure_reason ? ` (${inv.closure_reason})` : ''}
            </dd>
          </div>
        ) : null}
        {p.account?.status === 'disabled' && p.account.disabled_reason ? (
          <div>
            <dt className="text-ink-soft">Disabled</dt>
            <dd>{fmtDate(p.account.disabled_at)}: {p.account.disabled_reason}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-ink-soft">Access</dt>
          <dd data-testid="person-access">
            {summary.length === 0 ? 'None' : (
              <ul className="list-disc pl-5">
                {summary.map((s) => <li key={s}>{s}</li>)}
              </ul>
            )}
          </dd>
        </div>
      </dl>
      {p.is_self ? (
        <p className="mt-3 text-[13px] text-ink-soft">Your own account: another administrator changes it.</p>
      ) : actions.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {actions.includes('invite') ? <Button onClick={props.onInvite} data-testid="open-invite">Invite</Button> : null}
          {actions.includes('resend') ? <Button variant="secondary" onClick={props.onResend}>Resend invitation</Button> : null}
          {actions.includes('expire') ? <Button variant="secondary" onClick={props.onExpire}>Expire invitation</Button> : null}
          {actions.includes('access') ? <Button variant="secondary" onClick={props.onAccess} data-testid="open-access">Change access</Button> : null}
          {actions.includes('suspend') ? <Button variant="secondary" onClick={() => props.onLifecycle('suspend')}>Suspend</Button> : null}
          {actions.includes('reactivate') ? <Button variant="secondary" onClick={() => props.onLifecycle('reactivate')}>Reactivate</Button> : null}
          {actions.includes('disable') ? <Button variant="danger" onClick={() => props.onLifecycle('disable')}>Disable</Button> : null}
        </div>
      ) : null}
      {p.history.length > 0 ? (
        <details className="mt-3 text-sm">
          <summary className="tap cursor-pointer font-semibold">Account history ({p.history.length})</summary>
          <ol className="mt-2 space-y-1">
            {p.history.map((h, i) => (
              <li key={`${h.account_id}-${i}`}>
                {fmtDateTime(h.changed_at)}: {h.from_status ?? 'new'} &rarr; {h.to_status}, {h.reason}
                {h.changed_by ? ` (${h.changed_by_kind === 'self_activation' ? 'first sign-in' : h.changed_by})` : ''}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </Card>
  )
}

/** Per-site and global access checkboxes. */
function AccessPicker({ sites, value, onChange }: { sites: Sites; value: AccessSet; onChange: (v: AccessSet) => void }) {
  return (
    <div className="space-y-3">
      {sites.map((s) => (
        <fieldset key={s.id} className="rounded-lg border border-line bg-white p-3">
          <legend className="px-1 text-sm font-semibold">{s.name}</legend>
          {SITE_CAPABILITIES.map((c) => (
            <Checkbox
              key={c}
              checked={value.has(accessKey(c, s.id))}
              onChange={(on) => onChange(toggleAccess(value, c, s.id, on))}
              label={CAPABILITY_LABELS[c].label}
              hint={CAPABILITY_LABELS[c].help}
            />
          ))}
        </fieldset>
      ))}
      <fieldset className="rounded-lg border border-line bg-white p-3">
        <legend className="px-1 text-sm font-semibold">Administration (all sites)</legend>
        {GLOBAL_CAPABILITIES.map((c) => (
          <Checkbox
            key={c}
            checked={value.has(accessKey(c, null))}
            onChange={(on) => onChange(toggleAccess(value, c, null, on))}
            label={CAPABILITY_LABELS[c].label}
            hint={CAPABILITY_LABELS[c].help}
          />
        ))}
      </fieldset>
    </div>
  )
}

/** Add person (0026) and invite them (0022) with the invitation emailed (0027), in one form. */
function AddPersonSheet({ organisations, sites, onClose }: {
  organisations: NonNullable<AccountAdminView['organisations']>
  sites: Sites
  onClose: () => void
}) {
  const add = useCommand('add_person')
  const invite = useCommand('invite_person')
  const ids = { name: useId(), org: useId(), email: useId(), ref: useId(), days: useId() }
  const [name, setName] = useState('')
  const [org, setOrg] = useState(organisations[0]?.id ?? '')
  const [email, setEmail] = useState('')
  const [ref, setRef] = useState('')
  const [days, setDays] = useState('7')
  const [grants, setGrants] = useState<AccessSet>(() => new Set(sites.length === 1 ? [accessKey('view_site', sites[0].id)] : []))
  const [errors, setErrors] = useState<{ name?: string | null; email?: string | null }>({})
  // Once the person record exists, a retry (e.g. after correcting the email) only invites.
  const [personId, setPersonId] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState<{ name: string; ready: Ready } | null>(null)

  async function submit() {
    const clean = name.trim().replace(/\s+/g, ' ')
    const next = { name: clean.length < 2 ? 'Enter their full name.' : null, email: emailProblem(email) }
    setErrors(next)
    if (next.name || next.email || !org) return
    let id = personId
    if (!id) {
      const res = await add.run({ display_name: clean, employer_organisation_id: org, staff_reference: ref.trim() || null }).catch(() => null)
      if (!res) return
      id = String(res.person_id)
      setPersonId(id)
    }
    const inv = await invite
      .run({ person_id: id, email: normaliseEmail(email), grants: accessToGrants(grants), valid_days: Number(days) })
      .catch(() => null)
    if (!inv) return
    setSending(true)
    const sent = await emailInvitation(String(inv.invitation_id))
    setSending(false)
    setDone({ name: clean, ready: { email: String(inv.email ?? normaliseEmail(email)), expires_at: String(inv.expires_at), sent } })
  }

  return (
    <Sheet open title="Add and invite a person" onClose={onClose}>
      {done ? (
        <InvitationReady name={done.name} ready={done.ready} onClose={onClose} />
      ) : (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <ErrorBanner error={invite.error ?? (personId ? null : add.error)} />
          <Field label="Full name" htmlFor={ids.name} error={errors.name} required>
            <TextInput id={ids.name} value={name} onChange={(e) => setName(e.target.value)} disabled={Boolean(personId)} autoComplete="off" data-testid="add-person-name" />
          </Field>
          <Field label="Email" htmlFor={ids.email} error={errors.email} required hint="Their own address, never a shared mailbox.">
            <TextInput id={ids.email} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" data-testid="add-person-email" />
          </Field>
          <div className="grid gap-x-4 sm:grid-cols-2">
            {organisations.length > 1 ? (
              <Field label="Employer" htmlFor={ids.org} required>
                <Select id={ids.org} value={org} onChange={(e) => setOrg(e.target.value)} disabled={Boolean(personId)} options={organisations.map((o) => ({ value: o.id, label: o.name }))} />
              </Field>
            ) : null}
            <Field label="Staff number (optional)" htmlFor={ids.ref}>
              <TextInput id={ids.ref} value={ref} onChange={(e) => setRef(e.target.value)} disabled={Boolean(personId)} />
            </Field>
            <Field label="Invitation lasts" htmlFor={ids.days}>
              <Select id={ids.days} value={days} onChange={(e) => setDays(e.target.value)} options={INVITATION_DAYS.map((d) => ({ value: String(d), label: `${d} ${d === 1 ? 'day' : 'days'}` }))} />
            </Field>
          </div>
          <p className="mb-2 text-sm font-semibold">Access</p>
          <AccessPicker sites={sites} value={grants} onChange={setGrants} />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="submit" loading={add.isPending || invite.isPending || sending} data-testid="add-person-submit">Add and email invitation</Button>
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      )}
    </Sheet>
  )
}

function InviteSheet({ person, sites, onClose }: { person: AdminPerson; sites: Sites; onClose: () => void }) {
  const cmd = useCommand('invite_person')
  const ids = { email: useId(), days: useId(), note: useId() }
  const [email, setEmail] = useState('')
  const [days, setDays] = useState('7')
  const [note, setNote] = useState('')
  const [grants, setGrants] = useState<AccessSet>(() => new Set(sites.length === 1 ? [accessKey('view_site', sites[0].id)] : []))
  const [emailError, setEmailError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState<Ready | null>(null)

  async function submit() {
    const problem = emailProblem(email)
    setEmailError(problem)
    if (problem) return
    const res = await cmd
      .run({ person_id: person.person_id, email: normaliseEmail(email), grants: accessToGrants(grants), valid_days: Number(days), note: note.trim() || null })
      .catch(() => null)
    if (!res) return
    setSending(true)
    const sent = await emailInvitation(String(res.invitation_id))
    setSending(false)
    setDone({ email: String(res.email ?? normaliseEmail(email)), expires_at: String(res.expires_at), sent })
  }

  return (
    <Sheet open title={`Invite ${person.display_name}`} onClose={onClose}>
      {done ? (
        <InvitationReady name={person.display_name} ready={done} onClose={onClose} />
      ) : (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <ErrorBanner error={cmd.error} />
          <Field label="Their own email address" htmlFor={ids.email} error={emailError} required hint="They sign in with codes sent to this address. Never use a shared mailbox.">
            <TextInput id={ids.email} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" />
          </Field>
          <p className="mb-2 text-sm font-semibold">Access</p>
          <AccessPicker sites={sites} value={grants} onChange={setGrants} />
          <div className="mt-4 grid gap-x-4 sm:grid-cols-2">
            <Field label="Invitation lasts" htmlFor={ids.days}>
              <Select id={ids.days} value={days} onChange={(e) => setDays(e.target.value)} options={INVITATION_DAYS.map((d) => ({ value: String(d), label: `${d} ${d === 1 ? 'day' : 'days'}` }))} />
            </Field>
            <Field label="Note (optional)" htmlFor={ids.note}>
              <TextInput id={ids.note} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" loading={cmd.isPending || sending} data-testid="send-invite">Invite and email</Button>
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      )}
    </Sheet>
  )
}

type Ready = { email: string; expires_at: string; sent: InviteEmailResult }

function InvitationReady({ name, ready, onClose }: { name: string; ready: Ready; onClose: () => void }) {
  const { email, expires_at: expiresAt, sent } = ready
  const text = invitationMessage({ name, email, signInUrl: `${window.location.origin}/sign-in`, expiresAt, fmt: fmtDateTime })
  return (
    <div className="space-y-3" data-testid="invitation-ready" data-email-outcome={sent.ok ? sent.outcome : 'failed'}>
      {sent.ok ? (
        <Banner tone="ok" title={sent.outcome === 'invitation_sent' ? `Invitation emailed to ${email}` : `Sign-in code emailed to ${email}`} />
      ) : (
        <>
          <Banner tone="warn" title="Email not sent">{sent.message} Send them this instead:</Banner>
          <pre className="whitespace-pre-wrap rounded-lg border border-line bg-white p-3 text-sm">{text}</pre>
        </>
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={onClose}>Done</Button>
        <CopyButton text={text} label="Copy message" />
      </div>
    </div>
  )
}

function ResendSheet({ person, onClose }: { person: AdminPerson; onClose: () => void }) {
  const cmd = useCommand('resend_invitation')
  const inv = openInvitation(person)
  const [days, setDays] = useState('7')
  const id = useId()
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState<Ready | null>(null)
  if (!inv) return null
  return (
    <Sheet open title={`Resend the invitation to ${person.display_name}`} onClose={onClose}>
      {done ? (
        <InvitationReady name={person.display_name} ready={done} onClose={onClose} />
      ) : (
        <div>
          <ErrorBanner error={cmd.error} />
          <p className="mb-3 text-sm">A new invitation for {inv.email} with the same access replaces the old one.</p>
          <Field label="Invitation lasts" htmlFor={id}>
            <Select id={id} value={days} onChange={(e) => setDays(e.target.value)} options={INVITATION_DAYS.map((d) => ({ value: String(d), label: `${d} ${d === 1 ? 'day' : 'days'}` }))} />
          </Field>
          <Button
            loading={cmd.isPending || sending}
            onClick={async () => {
              const res = await cmd.run({ invitation_id: inv.id, valid_days: Number(days) }).catch(() => null)
              if (!res) return
              setSending(true)
              const sent = await emailInvitation(String(res.invitation_id))
              setSending(false)
              setDone({ email: inv.email, expires_at: String(res.expires_at), sent })
            }}
          >
            Resend and email
          </Button>
        </div>
      )}
    </Sheet>
  )
}

function ReasonForm({ label, button, danger, busy, onSubmit, children }: {
  label: string
  button: string
  danger?: boolean
  busy: boolean
  onSubmit: (reason: string) => void
  children?: ReactNode
}) {
  const id = useId()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        if (!reason.trim()) {
          setError('Give the reason; it is kept with the change.')
          return
        }
        onSubmit(reason.trim())
      }}
    >
      {children}
      <Field label={label} htmlFor={id} error={error} required>
        <TextArea id={id} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reason" />
      </Field>
      <Button type="submit" variant={danger ? 'danger' : 'primary'} loading={busy}>{button}</Button>
    </form>
  )
}

function ExpireSheet({ person, onClose }: { person: AdminPerson; onClose: () => void }) {
  const cmd = useCommand('expire_invitation')
  const inv = openInvitation(person)
  if (!inv) return null
  return (
    <Sheet open title={`Expire the invitation to ${person.display_name}`} onClose={onClose}>
      <ErrorBanner error={cmd.error} />
      <ReasonForm
        label="Reason"
        button="Expire now"
        busy={cmd.isPending}
        onSubmit={async (reason) => {
          const res = await cmd.run({ invitation_id: inv.id, reason }).catch(() => null)
          if (res) onClose()
        }}
      >
        <p className="mb-3 text-sm">{inv.email} can no longer use this invitation to sign in.</p>
      </ReasonForm>
    </Sheet>
  )
}

const LIFECYCLE_TEXT: Record<Lifecycle, { title: string; body: string; button: string }> = {
  suspend: { title: 'Suspend', body: 'They cannot read or sign anything until an administrator reactivates the account. Their access stays recorded.', button: 'Suspend account' },
  reactivate: { title: 'Reactivate', body: 'They can sign in and use their current access again.', button: 'Reactivate account' },
  disable: { title: 'Disable', body: 'Disabling is final: the account and all its access end now. Use it when someone leaves. To return later they need a new invitation.', button: 'Disable account' },
}

function LifecycleSheet({ person, action, onClose }: { person: AdminPerson; action: Lifecycle; onClose: () => void }) {
  const suspend = useCommand('suspend_account')
  const reactivate = useCommand('reactivate_account')
  const disable = useCommand('disable_account')
  const cmd = { suspend, reactivate, disable }[action]
  const text = LIFECYCLE_TEXT[action]
  if (!person.account) return null
  const accountId = person.account.id
  return (
    <Sheet open title={`${text.title} ${person.display_name}`} onClose={onClose}>
      <ErrorBanner error={cmd.error} />
      <ReasonForm
        label="Reason"
        button={text.button}
        danger={action !== 'reactivate'}
        busy={cmd.isPending}
        onSubmit={async (reason) => {
          const res = await cmd.run({ account_id: accountId, reason }).catch(() => null)
          if (res) onClose()
        }}
      >
        <p className="mb-3 text-sm">{text.body}</p>
      </ReasonForm>
    </Sheet>
  )
}

function AccessSheet({ person, sites, onClose }: { person: AdminPerson; sites: Sites; onClose: () => void }) {
  const queryClient = useQueryClient()
  const from = currentAccess(person)
  const [to, setTo] = useState<AccessSet>(() => new Set(from))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const account = person.account
  if (!account) return null
  const changes = (reason: string) => accessChanges(account.id, from, to, reason)
  return (
    <Sheet open title={`Access for ${person.display_name}`} onClose={onClose}>
      <ErrorBanner error={error} title="Not every change was made. The list shows what is recorded now." />
      <ReasonForm
        label="Reason for the change"
        button="Save access"
        busy={busy}
        onSubmit={async (reason) => {
          const list = changes(reason)
          if (list.length === 0) {
            onClose()
            return
          }
          setBusy(true)
          setError(null)
          try {
            // One command per change, each with its own key; a retry after a failure works
            // from the recorded state, so nothing is granted twice.
            for (const args of list) await runCommand('manage_access_grant', args, { idempotencyKey: newIdempotencyKey() })
            await queryClient.invalidateQueries()
            onClose()
          } catch (e) {
            setError(e)
            await queryClient.invalidateQueries()
          } finally {
            setBusy(false)
          }
        }}
      >
        <AccessPicker sites={sites} value={to} onChange={setTo} />
        <p className="my-3 text-sm text-ink-soft">Removing access takes effect at once: their next action at that site is refused.</p>
      </ReasonForm>
    </Sheet>
  )
}
